// /home/labdoo/zitech-nexus/frontend/src/contexts/AnnouncementContext.js
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { announcementAPI } from '../services/api';

// Create the context
const AnnouncementContext = createContext();

// Custom hook to use the announcement context
export const useAnnouncements = () => {
  const context = useContext(AnnouncementContext);
  if (!context) {
    throw new Error('useAnnouncements must be used within AnnouncementProvider');
  }
  return context;
};

// Provider component
export const AnnouncementProvider = ({ children }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [activeAnnouncement, setActiveAnnouncement] = useState(null);
  const [allActiveAnnouncements, setAllActiveAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(true);

  // Check authentication status
  const checkAuth = useCallback(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setIsAuthenticated(false);
      return false;
    }
    setIsAuthenticated(true);
    return true;
  }, []);

  // Helper to find active announcement from list
  const updateActiveFromList = useCallback((list) => {
    if (!list || !Array.isArray(list)) {
      setActiveAnnouncement(null);
      setAllActiveAnnouncements([]);
      localStorage.removeItem('active_announcement');
      localStorage.removeItem('all_active_announcements');
      return;
    }

    const now = new Date().toISOString();
    const activeList = list.filter(
      a => a.is_active && 
      a.start_date <= now && 
      (!a.end_date || a.end_date >= now)
    );
    
    // Store all active announcements
    setAllActiveAnnouncements(activeList);
    localStorage.setItem('all_active_announcements', JSON.stringify(activeList));
    
    // Store the first one as the main active announcement for backward compatibility
    if (activeList.length > 0) {
      setActiveAnnouncement(activeList[0]);
      localStorage.setItem('active_announcement', JSON.stringify(activeList[0]));
    } else {
      setActiveAnnouncement(null);
      localStorage.removeItem('active_announcement');
    }
  }, []);

  // Fetch active announcements - PUBLIC (no auth required)
  const fetchActiveAnnouncements = useCallback(async () => {
    try {
      const response = await announcementAPI.getActivePublic();
      
      // Handle the response - it has { active_count, main, all }
      if (response.data) {
        // Store all active announcements
        if (response.data.all && Array.isArray(response.data.all)) {
          setAllActiveAnnouncements(response.data.all);
          localStorage.setItem('all_active_announcements', JSON.stringify(response.data.all));
        } else {
          setAllActiveAnnouncements([]);
          localStorage.removeItem('all_active_announcements');
        }
        
        // Store the main/first one as activeAnnouncement for backward compatibility
        if (response.data.main) {
          setActiveAnnouncement(response.data.main);
          localStorage.setItem('active_announcement', JSON.stringify(response.data.main));
        } else if (response.data.all && response.data.all.length > 0) {
          setActiveAnnouncement(response.data.all[0]);
          localStorage.setItem('active_announcement', JSON.stringify(response.data.all[0]));
        } else {
          setActiveAnnouncement(null);
          localStorage.removeItem('active_announcement');
        }
      } else {
        setActiveAnnouncement(null);
        setAllActiveAnnouncements([]);
        localStorage.removeItem('active_announcement');
        localStorage.removeItem('all_active_announcements');
      }
    } catch (error) {
      console.error('Error fetching active announcements:', error);
      // Fallback to localStorage
      try {
        const savedActive = localStorage.getItem('active_announcement');
        if (savedActive) {
          setActiveAnnouncement(JSON.parse(savedActive));
        }
        
        const savedAll = localStorage.getItem('all_active_announcements');
        if (savedAll) {
          setAllActiveAnnouncements(JSON.parse(savedAll));
        } else {
          // Try to get from announcements list
          const savedList = localStorage.getItem('announcements');
          if (savedList) {
            const parsed = JSON.parse(savedList);
            updateActiveFromList(parsed);
          }
        }
      } catch (e) {
        console.error('Error loading from localStorage:', e);
      }
    }
  }, [updateActiveFromList]);

  // Fetch all announcements - PRIVATE (auth required)
  const fetchAnnouncements = useCallback(async () => {
    if (!checkAuth()) {
      // Try to load from localStorage as fallback
      try {
        const saved = localStorage.getItem('announcements');
        if (saved) {
          const parsed = JSON.parse(saved);
          setAnnouncements(parsed);
          updateActiveFromList(parsed);
        }
      } catch (e) {
        console.error('Error loading from localStorage:', e);
      }
      return;
    }

    setLoading(true);
    try {
      const response = await announcementAPI.getAll();
      const data = response.data || [];
      setAnnouncements(data);
      // Save to localStorage as backup
      localStorage.setItem('announcements', JSON.stringify(data));
      // Also update active announcements
      await fetchActiveAnnouncements();
    } catch (error) {
      console.error('Error fetching announcements:', error);
      // Fallback to localStorage
      try {
        const saved = localStorage.getItem('announcements');
        if (saved) {
          const parsed = JSON.parse(saved);
          setAnnouncements(parsed);
          updateActiveFromList(parsed);
        }
      } catch (e) {
        console.error('Error loading from localStorage:', e);
      }
    } finally {
      setLoading(false);
    }
  }, [checkAuth, fetchActiveAnnouncements, updateActiveFromList]);

  // Load active announcements on mount (public, no auth needed)
  useEffect(() => {
    fetchActiveAnnouncements();
    // Refresh active announcements every 5 minutes
    const interval = setInterval(fetchActiveAnnouncements, 300000);
    return () => clearInterval(interval);
  }, [fetchActiveAnnouncements]);

  // Load all announcements if authenticated
  useEffect(() => {
    if (checkAuth()) {
      fetchAnnouncements();
    }
  }, [checkAuth, fetchAnnouncements]);

  // Create a new announcement
  const createAnnouncement = async (announcementData) => {
    if (!checkAuth()) {
      throw new Error('Please log in to create announcements');
    }
    
    setLoading(true);
    try {
      const response = await announcementAPI.create(announcementData);
      const newAnnouncement = response.data;
      setAnnouncements(prev => {
        const updatedList = [...prev, newAnnouncement];
        localStorage.setItem('announcements', JSON.stringify(updatedList));
        return updatedList;
      });
      
      // Check if this announcement should be active
      const now = new Date().toISOString();
      if (newAnnouncement.is_active && 
          newAnnouncement.start_date <= now && 
          (!newAnnouncement.end_date || newAnnouncement.end_date >= now)) {
        // Refresh active announcements
        await fetchActiveAnnouncements();
      }
      
      setLoading(false);
      return newAnnouncement;
    } catch (error) {
      console.error('Error creating announcement:', error);
      setLoading(false);
      throw error;
    }
  };

  // Update an existing announcement
  const updateAnnouncement = async (id, updatedData) => {
    if (!checkAuth()) {
      throw new Error('Please log in to update announcements');
    }
    
    setLoading(true);
    try {
      const response = await announcementAPI.update(id, updatedData);
      const updated = response.data;
      
      setAnnouncements(prev => {
        const updatedList = prev.map(announcement => 
          announcement.id === id ? updated : announcement
        );
        localStorage.setItem('announcements', JSON.stringify(updatedList));
        return updatedList;
      });
      
      // Refresh active announcements
      await fetchActiveAnnouncements();
      
      setLoading(false);
      return updated;
    } catch (error) {
      console.error('Error updating announcement:', error);
      setLoading(false);
      throw error;
    }
  };

  // Delete an announcement
  const deleteAnnouncement = async (id) => {
    if (!checkAuth()) {
      throw new Error('Please log in to delete announcements');
    }
    
    setLoading(true);
    try {
      await announcementAPI.delete(id);
      setAnnouncements(prev => {
        const filteredList = prev.filter(a => a.id !== id);
        localStorage.setItem('announcements', JSON.stringify(filteredList));
        return filteredList;
      });
      
      // Refresh active announcements
      await fetchActiveAnnouncements();
      
      setLoading(false);
    } catch (error) {
      console.error('Error deleting announcement:', error);
      setLoading(false);
      throw error;
    }
  };

  // Toggle announcement active status
  const toggleAnnouncementStatus = async (id) => {
    if (!checkAuth()) {
      throw new Error('Please log in to toggle announcement status');
    }
    
    setLoading(true);
    try {
      const response = await announcementAPI.toggleStatus(id);
      const updated = response.data.announcement;
      
      setAnnouncements(prev => {
        const updatedList = prev.map(announcement => 
          announcement.id === id ? updated : announcement
        );
        localStorage.setItem('announcements', JSON.stringify(updatedList));
        return updatedList;
      });
      
      // Refresh active announcements
      await fetchActiveAnnouncements();
      
      setLoading(false);
      return updated;
    } catch (error) {
      console.error('Error toggling announcement status:', error);
      setLoading(false);
      throw error;
    }
  };

  // Get announcement by ID
  const getAnnouncementById = useCallback((id) => {
    return announcements.find(a => a.id === id);
  }, [announcements]);

  // Get all active announcements
  const getActiveAnnouncements = useCallback(() => {
    const now = new Date().toISOString();
    return announcements.filter(
      a => a.is_active && 
      a.start_date <= now && 
      (!a.end_date || a.end_date >= now)
    );
  }, [announcements]);

  // Refresh active announcements
  const refreshActiveAnnouncements = useCallback(async () => {
    await fetchActiveAnnouncements();
  }, [fetchActiveAnnouncements]);

  // Force refresh all announcements
  const refreshAllAnnouncements = useCallback(async () => {
    await fetchAnnouncements();
    await fetchActiveAnnouncements();
  }, [fetchAnnouncements, fetchActiveAnnouncements]);

  // The value object that will be provided to consumers
  const value = {
    announcements,
    activeAnnouncement,
    allActiveAnnouncements, // NEW: All active announcements for auto-scrolling
    loading,
    isAuthenticated,
    createAnnouncement,
    updateAnnouncement,
    deleteAnnouncement,
    toggleAnnouncementStatus,
    getAnnouncementById,
    getActiveAnnouncements,
    refreshActiveAnnouncements,
    refreshAllAnnouncements,
    setActiveAnnouncement,
    fetchAnnouncements,
    fetchActiveAnnouncements
  };

  return (
    <AnnouncementContext.Provider value={value}>
      {children}
    </AnnouncementContext.Provider>
  );
};

export default AnnouncementContext;